import { useParams, useNavigate } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';
import { categoryResults } from '@/mocks/results';
import { vendors } from '@/mocks/vendors';

export default function Result() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const cat = category as keyof typeof categoryData;
  const catInfo = categoryData[cat];
  const result = categoryResults[cat];

  const matchedVendors = result?.matchedVendors
    ? vendors.filter((v) => result.matchedVendors.includes(v.id))
    : [];

  if (!catInfo || !result) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center">
        <p>존재하지 않는 카테고리입니다</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="bg-navy px-5 pt-8 pb-8">
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate('/home')}
            className="w-8 h-8 flex items-center justify-center text-cream cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color }}
          >
            <i className={`${catInfo.icon} text-lg`} />
          </div>
          <p className="text-cream-dark text-sm font-medium">{catInfo.name} 준비 완료</p>
        </div>
        <h1 className="font-serif-display text-white text-2xl font-bold leading-snug">
          {result.title}
        </h1>
        <p className="text-cream-dark text-sm mt-2 leading-relaxed">{result.subtitle}</p>
      </div>

      <div className="flex-1 px-5 py-6 overflow-y-auto">
        <div className="flex flex-wrap gap-2 mb-6">
          {result.tags.map((tag, i) => (
            <span
              key={i}
              className="px-3 py-1.5 rounded-full bg-navy/10 text-navy text-xs font-semibold border border-navy/10"
            >
              {tag}
            </span>
          ))}
        </div>

        <p className="text-navy font-bold text-sm mb-3">분석 요약</p>
        <div className="space-y-3 mb-8">
          {result.summaryCards.map((card, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 border border-cream-dark"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${catInfo.color}12`, color: catInfo.color }}
                >
                  <i className={`${card.icon} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-navy font-semibold text-sm mb-1">{card.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{card.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-navy rounded-2xl p-5 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-softgold/20 flex items-center justify-center">
              <i className="ri-bar-chart-line text-softgold text-sm" />
            </div>
            <p className="text-white font-semibold text-sm">{result.analysisTitle}</p>
          </div>
          <div className="space-y-3">
            {result.analysisPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-softgold mt-0.5 shrink-0 text-xs font-bold">{i + 1}.</span>
                <span className="text-cream-dark text-xs leading-relaxed">{point}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <p className="text-navy font-bold text-sm mb-3">
            이 기준에 맞는 인증 {catInfo.name}점
          </p>
          <div className="space-y-3">
            {matchedVendors.map((vendor) => (
              <button
                key={vendor.id}
                onClick={() => navigate(`/vendor/${vendor.id}`)}
                className="w-full bg-white rounded-2xl border border-cream-dark overflow-hidden text-left cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div className="flex">
                  <div className="w-[100px] h-[100px] shrink-0 overflow-hidden">
                    <img
                      src={vendor.image}
                      alt={vendor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-navy text-sm truncate">{vendor.name}</p>
                      <span className="px-1.5 py-0.5 rounded bg-softgold/15 text-softgold text-[10px] font-bold shrink-0">
                        {vendor.matchRate}% 매칭
                      </span>
                    </div>
                    <p className="text-beige-dark text-xs mb-2">{vendor.region} · {vendor.priceRange}</p>
                    <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                      {vendor.recommendReason}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {vendor.tags.slice(0, 2).map((tag, i) => (
                        <span
                          key={i}
                          className="px-1.5 py-0.5 rounded bg-cream text-beige-dark text-[10px]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => navigate(`/guide/${cat}`)}
          className="w-full bg-white rounded-xl p-4 border border-cream-dark flex items-center justify-between text-left cursor-pointer hover:bg-cream transition-colors mb-6"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${catInfo.color}12`, color: catInfo.color }}
            >
              <i className="ri-book-open-line text-sm" />
            </div>
            <div>
              <p className="text-navy font-medium text-sm">{catInfo.name} 준비 가이드 다시보기</p>
              <p className="text-beige-dark text-xs">기준과 체크포인트를 확인하세요</p>
            </div>
          </div>
          <i className="ri-arrow-right-s-line text-beige-dark" />
        </button>
      </div>

      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3 bg-white border-t border-cream-dark shrink-0 space-y-2">
        <button
          onClick={() => navigate('/quote')}
          className="w-full bg-softgold text-navy-dark rounded-xl py-4 font-bold text-base cursor-pointer hover:bg-softgold-dark transition-colors whitespace-nowrap"
        >
          견적 요청하기
        </button>
        <button
          onClick={() => navigate(`/vendors?category=${cat}`)}
          className="w-full bg-white text-navy rounded-xl py-3 font-medium text-sm border border-cream-dark cursor-pointer hover:bg-cream transition-colors whitespace-nowrap"
        >
          {catInfo.name} 업체 전체 보기
        </button>
      </div>
    </div>
  );
}